import { useState, useCallback, useMemo, useRef } from 'react';
import type { DateRange, ShowFilter, DecibelTableColumn } from '@/types';
import type { DeviceDownloadsResponse } from '@/types/api/devices';
import { Button, DataTable, Loader } from '@acast-tech/decibel';
import { ArrowDownToLineLightGlyph, ChevronRightRegularGlyph } from '@acast-tech/decibel-glyphs';
import useDeviceDownloadsCsvExport from './useDeviceDownloadsCsvExport';
import {
  trackAudienceTopDevicesModalDeviceSelectionToggled,
  trackAudienceTopDevicesModalDownloadClicked,
  trackAudienceTopDevicesModalOpened,
} from './audienceModalMixpanel';
import { Line } from 'recharts';
import Modal from '@/components/modals/Modal/Modal';
import TimeSeriesLineChart from '@/components/charts/TimeSeriesLineChart';
import { useDeviceDownloadsQuery } from '@/services/revenueApi';
import { useDerivedNetworkContext } from '@/context';
import { useFlags } from 'launchdarkly-react-client-sdk';
import NoDataMessage from '@/components/states/NoDataMessage';
import { formatPercentageTwoDp, formatBigNumbers } from '@/utils/numberFormattingUtils';
import { createGranularityXAxisFormatter } from '@/utils';
import { shortMonthNames } from '@/utils/dateUtils';
import { useColorAssignment } from '@/hooks/useColorAssignment';
import { checkboxColors } from '@/utils/colorUtils';
import Checkbox from '@/components/Checkbox';

type DeviceRow = {
  key: string;
  device: string;
  downloads: number;
  percentage: number;
  selected: boolean;
  color: string | undefined;
};

type DevicesChartDataPoint = { date: string; [device: string]: number | string };

const formatXAxisDate = createGranularityXAxisFormatter('day');

const DevicesChartTooltipContent = ({
  payload,
  selectedIds,
  getColor,
}: {
  payload: DevicesChartDataPoint;
  selectedIds: string[];
  getColor: (id: string) => number | undefined;
}) => {
  if (!payload?.date) return null;

  const [, month, day] = (payload.date as string).split('-');
  const formattedDate = `${shortMonthNames[parseInt(month) - 1]} ${day}`;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium mr-6">{formattedDate}</p>
      {selectedIds.map((device) => {
        const value = payload[device];
        if (value == null) return null;
        const colorIndex = getColor(device);
        const color = colorIndex != null ? checkboxColors[colorIndex] : undefined;
        return (
          <p className="text-xs inline-flex items-center gap-1" key={device}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
            <span className="truncate max-w-[140px]">{device}:</span>
            <strong>{(value as number).toLocaleString()}</strong>
          </p>
        );
      })}
    </div>
  );
};

const DevicesChart = ({
  graph,
  selectedIds,
  getColor,
}: {
  graph: DeviceDownloadsResponse['graph'];
  selectedIds: string[];
  getColor: (id: string) => number | undefined;
}) => {
  const chartData = useMemo<DevicesChartDataPoint[]>(() => {
    const dateMap = new Map<string, DevicesChartDataPoint>();
    for (const point of graph) {
      if (!dateMap.has(point.date)) {
        dateMap.set(point.date, { date: point.date });
      }
      dateMap.get(point.date)![point.device] = point.downloads;
    }
    return Array.from(dateMap.values());
  }, [graph]);

  return (
    <TimeSeriesLineChart
      data={chartData}
      dateKey="date"
      xAxisTickFormatter={formatXAxisDate}
      yAxisFormatter={(value) => (value !== 0 ? formatBigNumbers(value) : '')}
      ariaLabel="Device downloads over time"
      tooltipContent={(payload) => (
        <DevicesChartTooltipContent
          payload={payload}
          selectedIds={selectedIds}
          getColor={getColor}
        />
      )}
      height={300}
    >
      {selectedIds.map((device) => (
        <Line
          key={device}
          dataKey={device}
          stroke={checkboxColors[getColor(device) ?? 0]}
          dot={false}
          connectNulls={true}
          strokeWidth={2}
          type="linear"
        />
      ))}
    </TimeSeriesLineChart>
  );
};

const buildDeviceTableData = (
  devices: DeviceDownloadsResponse['table']['devices'],
  selectedIds: string[],
  getColor: (id: string) => number | undefined
): DeviceRow[] =>
  devices.map((item) => {
    const colorIndex = getColor(item.device);
    return {
      key: item.device,
      device: item.device,
      downloads: item.downloads,
      percentage: item.percentOfTotal,
      selected: selectedIds.includes(item.device),
      color: colorIndex !== undefined ? checkboxColors[colorIndex] : undefined,
    };
  });

const buildDeviceTableColumns = (toggle: (id: string) => void): DecibelTableColumn<DeviceRow>[] => [
  {
    id: 'checkbox',
    title: '',
    width: '40px',
    render: (row) => (
      <Checkbox
        name={row.key}
        checked={row.selected}
        onChange={() => toggle(row.key)}
        color={row.color}
      />
    ),
  },
  { id: 'device', title: 'Device', render: (row) => row.device },
  {
    id: 'downloads',
    title: 'Downloads',
    render: (row) => row.downloads.toLocaleString(),
    align: 'right',
    width: '160px',
  },
  {
    id: 'percentage',
    title: '% of Total',
    render: (row) => `${formatPercentageTwoDp(row.percentage)}%`,
    align: 'right',
    width: '100px',
  },
];

/**
 * Rendered only when data is available, so useState can be initialised
 * directly from the data without a useEffect or an empty-means-all workaround.
 */
const TopDevicesTable = ({ data }: { data: DeviceDownloadsResponse }) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    data.table.devices.map((item) => item.device)
  );
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const { getColor } = useColorAssignment(selectedIds);

  const toggle = useCallback((id: string) => {
    const isSelected = selectedIdsRef.current.includes(id);
    trackAudienceTopDevicesModalDeviceSelectionToggled(id, !isSelected);
    setSelectedIds((prev) =>
      isSelected ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  }, []);

  return (
    <section className="px-6 py-10 flex flex-col gap-10">
      <DevicesChart graph={data.graph} selectedIds={selectedIds} getColor={getColor} />
      <DataTable
        title="devices"
        columns={buildDeviceTableColumns(toggle)}
        data={buildDeviceTableData(data.table.devices, selectedIds, getColor)}
        pagination={false}
        getRowId={(row) => row.key}
      />
    </section>
  );
};

const TopDevicesModalContent = ({
  dateRange,
  selectedShow,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
}) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { data, isPending } = useDeviceDownloadsQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, sources, granularity: 'day' }
  );

  if (isPending) {
    return (
      <section className="px-6 py-10 flex flex-col justify-center items-center h-[60vh] gap-10">
        <Loader data-testid="loader" />
      </section>
    );
  }

  if (!data || data.table.devices.length < 1) {
    return <NoDataMessage dataType="devices data" />;
  }

  return <TopDevicesTable data={data} />;
};

const TopDevicesModal = () => {
  const { handleDeviceDownloadsCSVExport } = useDeviceDownloadsCsvExport();

  return (
    <Modal
      hashProp="devices-details"
      title="Device details"
      showDateRangeFilter
      onOpen={trackAudienceTopDevicesModalOpened}
      downloadConfig={{
        onDownload: (dateRange, selectedShow) => {
          trackAudienceTopDevicesModalDownloadClicked();
          return handleDeviceDownloadsCSVExport(dateRange, selectedShow, 'day');
        },
        downloadButtonText: 'Export',
        downloadButtonIcon: ArrowDownToLineLightGlyph,
      }}
      renderTrigger={(openModal) => (
        <div className="flex flex-row justify-end self-end">
          <Button
            className="self-right"
            variant="tertiary"
            glyphEnd={ChevronRightRegularGlyph}
            onClick={openModal}
          >
            More details
          </Button>
        </div>
      )}
    >
      {(dateRange, selectedShow) => (
        <TopDevicesModalContent dateRange={dateRange} selectedShow={selectedShow} />
      )}
    </Modal>
  );
};

export default TopDevicesModal;
